<script setup>
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import authV2LoginIllustrationBorderedDark from '@images/pages/auth-v2-login-illustration-bordered-dark.png'
import authV2LoginIllustrationBorderedLight from '@images/pages/auth-v2-login-illustration-bordered-light.png'
import authV2LoginIllustrationDark from '@images/pages/auth-v2-login-illustration-dark.png'
import authV2LoginIllustrationLight from '@images/pages/auth-v2-login-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { onMounted, ref } from 'vue'

const authThemeImg = useGenerateImageVariant(authV2LoginIllustrationLight, authV2LoginIllustrationDark, authV2LoginIllustrationBorderedLight, authV2LoginIllustrationBorderedDark, true)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

definePage({
  meta: {
    layout: 'blank',
    unauthenticatedOnly: true,
  },
})

const route = useRoute()
const router = useRouter()

// RightBytes logo path
const RightBytesLogo = '/images/applications/RightBytes.png'

// SSO auto-login state
const ssoAttemptKey = 'sso_auto_login_attempt_count'
const maxAutoAttempts = 1
const autoLoginAttempted = ref(false)
const isAutoLoggingIn = ref(false)
const isSilentCheckInProgress = ref(false)

function signInToPersona() {
  // Go directly to Persona with login UI
  window.location.href = '/api/auth/redirect?prompt=login'
}

// Silent authentication check using hidden iframe
function performSilentAuthCheck() {
  return new Promise((resolve, reject) => {
    console.log('Starting silent authentication check...')
    
    // Create hidden iframe for silent auth
    const iframe = document.createElement('iframe')

    iframe.style.display = 'none'
    iframe.style.width = '0px'
    iframe.style.height = '0px'
    iframe.src = '/api/auth/redirect?prompt=none&silent=true'
    
    let timeoutId
    
    // Handle successful silent authentication
    const handleSuccess = () => {
      console.log('Silent authentication successful')
      cleanup()
      resolve(true)
    }
    
    // Handle failed silent authentication
    const handleFailure = error => {
      console.log('Silent authentication failed:', error)
      cleanup()
      resolve(false)
    }
    
    // Cleanup function
    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId)
      if (iframe.parentNode) {
        document.body.removeChild(iframe)
      }
      window.removeEventListener('message', messageHandler)
    }
    
    // Listen for messages from iframe
    const messageHandler = event => {
      // Verify origin for security
      if (event.origin !== window.location.origin) {
        return
      }
      
      if (event.data.type === 'SILENT_AUTH_SUCCESS') {
        // User is authenticated, redirect to app
        window.location.href = event.data.redirectUrl || '/'
      } else if (event.data.type === 'SILENT_AUTH_FAILED') {
        handleFailure(event.data.error)
      }
    }
    
    // Set up event listener
    window.addEventListener('message', messageHandler)
    
    // Set timeout for silent auth (10 seconds)
    timeoutId = setTimeout(() => {
      handleFailure('Silent auth timeout')
    }, 10000)
    
    // Handle iframe load errors
    iframe.onerror = () => {
      handleFailure('Iframe load error')
    }
    
    // Add iframe to DOM
    document.body.appendChild(iframe)
  })
}

// SSO auto-login functions
function getSSOAttemptCount() {
  const count = sessionStorage.getItem(ssoAttemptKey)
  
  return count ? parseInt(count, 10) : 0
}

function incrementSSOAttemptCount() {
  const currentCount = getSSOAttemptCount()

  sessionStorage.setItem(ssoAttemptKey, (currentCount + 1).toString())
}

function shouldAttemptAutoLogin() {
  const attemptCount = getSSOAttemptCount()
  
  return attemptCount < maxAutoAttempts && !autoLoginAttempted.value
}

async function attemptAutoSSO() {
  if (shouldAttemptAutoLogin()) {
    autoLoginAttempted.value = true
    isAutoLoggingIn.value = true
    isSilentCheckInProgress.value = true
    incrementSSOAttemptCount()
    
    console.log('Attempting silent SSO authentication...')
    
    try {
      // Try silent authentication first
      const silentAuthSuccess = await performSilentAuthCheck()
      
      if (!silentAuthSuccess) {
        // Silent auth failed, show login form after a brief delay
        setTimeout(() => {
          isAutoLoggingIn.value = false
          isSilentCheckInProgress.value = false
          console.log('Silent auth failed, showing manual login option')
        }, 500)
      }
    } catch (error) {
      console.error('Silent authentication error:', error)

      // Show manual login form on error
      isAutoLoggingIn.value = false
      isSilentCheckInProgress.value = false
    }
  }
}

// Initialize auto-login on component mount
onMounted(() => {
  // Check if we should attempt automatic login
  attemptAutoSSO()
})
</script>


<template>
  <div class="auth-wrapper d-flex">
    <!-- Left Side - Illustration (Hidden on mobile) -->
    <VCol
      lg="8"
      class="d-none d-lg-flex"
    >
      <div class="position-relative auth-bg rounded-lg w-100 ma-8 me-0">
        <div class="d-flex align-center justify-center w-100 h-100 pa-6">
          <!-- Restaurant/Food themed illustration -->
          <div class="illustration-container">
            <!-- Overlay Content -->
            <div class="overlay-content">
              <h2 class="text-h2 font-weight-bold text-white mb-4">
                RightBytes Portal
              </h2>
              <p class="text-h6 text-white opacity-90 mb-6">
                Streamline operations, track inventory, and boost efficiency with RightBytes's comprehensive restaurant management platform.
              </p>
              
              <!-- Feature highlights -->
              <div class="features-list">
                <div class="feature-item d-flex align-center mb-3">
                  <VIcon
                    icon="tabler-chart-line"
                    color="white"
                    class="me-3"
                  />
                  <span class="text-body-1 text-white">Real-time Analytics & Reports</span> 
                </div>
                <div class="feature-item d-flex align-center mb-3">
                  <VIcon
                    icon="tabler-package"
                    color="white"
                    class="me-3"
                  />
                  <span class="text-body-1 text-white">Smart Inventory Management</span>
                </div>
                <div class="feature-item d-flex align-center mb-3">
                  <VIcon
                    icon="tabler-users"
                    color="white"
                    class="me-3"
                  />
                  <span class="text-body-1 text-white">Staff Activity Monitoring</span>
                </div>
                <div class="feature-item d-flex align-center">
                  <VIcon
                    icon="tabler-device-desktop"
                    color="white"
                    class="me-3"
                  />
                  <span class="text-body-1 text-white">Multi-Device Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <VImg
          :src="authThemeMask"
          class="auth-footer-mask"
        />
      </div>
    </VCol>

    <!-- Right Side - Login Form -->
    <VCol
      cols="12"
      lg="4"
      class="d-flex align-center justify-center auth-form-section"
    >
      <div class="auth-card-container w-100">
        <!-- Mobile Logo (Shown only on mobile) -->
        <div class="d-flex d-lg-none justify-center mb-4">
          <VImg
            :src="RightBytesLogo"
            max-width="100"
            height="auto"
            contain
          />
        </div>

        <VCard 
          flat 
          :max-width="420" 
          class="mx-auto pa-4 pa-sm-6"
          elevation="0"
        >
          <VCardText class="pa-0">
            <!-- Desktop Logo -->
            <div class="d-none d-lg-flex justify-center mb-4">
              <VImg
                :src="RightBytesLogo"
                max-width="120"
                height="auto"
                contain
                class="logo-main"
              />
            </div>

            <!-- Welcome Text -->
            <div class="text-center mb-4">
              <h4 class="text-h4 font-weight-bold mb-2 text-primary">
                Welcome to InsightsLite
              </h4>
              <h6 class="text-h6 font-weight-medium mb-2">
                👋 Ready to get started?
              </h6>
              <p class="text-body-2 text-medium-emphasis mb-0">
                Sign in with your NCCO account to continue
              </p>
            </div>

            <!-- Auto SSO Loading Indicator -->
            <div
              v-if="isAutoLoggingIn"
              class="text-center mb-4"
            >
              <div class="auto-login-container">
                <VProgressCircular
                  indeterminate
                  color="primary"
                  size="40"
                  width="3"
                  class="mb-3"
                />
                <p class="text-body-2 text-primary font-weight-medium mb-2">
                  🔐 {{ isSilentCheckInProgress ? 'Checking Authentication' : 'Automatic SSO Login' }}
                </p>
                <p class="text-caption text-medium-emphasis">
                  {{ isSilentCheckInProgress ? 'Verifying if you\'re already signed in...' : 'Redirecting you to NCCO authentication...' }}
                </p>
              </div>
            </div>

            <!-- Sign In Button -->
            <div
              v-if="!isAutoLoggingIn"
              class="text-center mb-4"
            >
              <VBtn 
                block
                size="large" 
                color="primary"
                variant="flat"
                class="login-btn"
                prepend-icon="tabler-login"
                @click="signInToPersona"
              >
                Sign In
              </VBtn>
            </div>

            <!-- Helper Text -->
            <div
              v-if="!isAutoLoggingIn"
              class="text-center mb-3"
            >
              <p class="text-caption text-medium-emphasis">
                <VIcon
                  icon="tabler-info-circle"
                  size="14"
                  class="me-1"
                />
                You'll be redirected to NCCO's secure authentication system
              </p>
            </div>

            <!-- Security Features -->
            <div
              v-if="!isAutoLoggingIn"
              class="security-features-compact"
            >
              <div class="d-flex align-center justify-center mb-2">
                <VIcon
                  icon="tabler-shield-check"
                  color="success"
                  class="me-2"
                  size="16"
                />
                <span class="text-caption text-success">Enterprise Security</span>
                <span class="mx-2">•</span>
                <VIcon
                  icon="tabler-key"
                  color="info"
                  class="me-2"
                  size="16"
                />
                <span class="text-caption text-info">SSO Enabled</span>
              </div>
            </div>

            <!-- Footer -->
            <div class="text-center mt-3">
              <p class="text-caption text-medium-emphasis mb-1">
                © 2025 RightBytes NCCO. All rights reserved.
              </p>
              <p class="text-caption text-medium-emphasis">
                Need help? Contact <a
                  href="mailto:support@rightbytes.com"
                  class="text-primary"
                >support@rightbytes.com</a>
              </p>
            </div>
          </VCardText>
        </VCard>
      </div>
    </VCol>
  </div>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth";

.auth-wrapper {
  min-height: 100vh;
  min-height: 100dvh;
}

.auth-bg {
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 50%, #4b79a1 100%);
  position: relative;
  overflow: hidden;
}

.auth-bg::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.2);
  z-index: 1;
}

.illustration-container {
  position: relative;
  z-index: 2;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.illustration-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.1;
  z-index: -1;
}

.overlay-content {
  text-align: center;
  max-width: 500px;
  padding: 2rem;
  z-index: 2;
}

.features-list {
  text-align: left;
  max-width: 400px;
}

.feature-item {
  animation: slideInLeft 0.6s ease-out forwards;
  opacity: 0;
  transform: translateX(-30px);
}

.feature-item:nth-child(1) { animation-delay: 0.2s; }
.feature-item:nth-child(2) { animation-delay: 0.4s; }
.feature-item:nth-child(3) { animation-delay: 0.6s; }
.feature-item:nth-child(4) { animation-delay: 0.8s; }

@keyframes slideInLeft {
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.auth-form-section {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  height: 100vh;
  height: 100dvh;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  
  @media (max-width: 1024px) {
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    padding: 0.25rem;
  }
  
  @media (max-width: 600px) {
    padding: 0.5rem;
    align-items: flex-start;
    padding-top: 2rem;
  }
}

.auth-card-container {
  max-width: 500px;
  animation: fadeInUp 0.8s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.auto-login-container {
  padding: 1.5rem;
  border: 2px solid rgba(var(--v-theme-primary), 0.2);
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.05) 0%, rgba(var(--v-theme-primary), 0.1) 100%);
  animation: pulse 2s ease-in-out infinite alternate;
}

@keyframes pulse {
  from {
    background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.05) 0%, rgba(var(--v-theme-primary), 0.1) 100%);
    transform: scale(1);
  }
  to {
    background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.1) 0%, rgba(var(--v-theme-primary), 0.15) 100%);
    transform: scale(1.02);
  }
}

.logo-main {
  transition: transform 0.3s ease;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.logo-main:hover {
  transform: scale(1.05);
}

.login-btn {
  height: 56px;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  box-shadow: 0 8px 16px rgba(var(--v-theme-primary), 0.3);
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 12px 20px rgba(var(--v-theme-primary), 0.4);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Email form styling
.v-text-field {
  .v-field {
    border-radius: 12px;
    transition: all 0.3s ease;
    
    &:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }
    
    &.v-field--focused {
      box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.2);
    }
  }
}

.security-features {
  .v-icon {
    transition: transform 0.3s ease;
  }
  
  > div:hover .v-icon {
    transform: scale(1.1);
  }
}

.auth-footer-mask {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  z-index: 0;
  opacity: 0.7;
}

// Responsive Design
@media (max-width: 960px) {
  .auth-form-section {
    padding: 1rem;
  }
  
  .auth-card-container {
    padding: 0;
  }
}

@media (max-width: 600px) {
  .overlay-content {
    padding: 1rem;
    
    .text-h2 {
      font-size: 1.8rem !important;
    }
    
    .text-h6 {
      font-size: 1.1rem !important;
    }
  }
  
  .features-list {
    .text-body-1 {
      font-size: 0.9rem;
    }
  }
  
  .login-btn {
    height: 48px;
    font-size: 1rem;
  }
  
  .auth-card-container {
    max-width: 100%;
    padding: 0;
  }
  
  .text-h4 {
    font-size: 1.5rem !important;
  }
  
  .text-h6 {
    font-size: 1.1rem !important;
  }
}

// Dark theme adjustments
.v-theme--dark {
  .auth-form-section {
    background: linear-gradient(135deg, #1a1a1a 0%, #2d3748 100%);
  }
  
  .security-features {
    > div {
      opacity: 0.9;
    }
  }
}

// Custom scroll for mobile
.auth-form-section {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

// Loading animation for the logo
.logo-main {
  animation: logoGlow 2s ease-in-out infinite alternate;
}

@keyframes logoGlow {
  from {
    filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
  }
  to {
    filter: drop-shadow(0 4px 12px rgba(var(--v-theme-primary), 0.2));
  }
}

// Enhanced hover effects
.feature-item {
  transition: all 0.3s ease;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateX(10px);
  }
}

// Professional styling for links
a {
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    color: rgb(var(--v-theme-primary)) !important;
    text-decoration: underline;
  }
}
</style>
